import React from "react";
import { ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, Clock, TrendingUp } from "lucide-react";

function ScoreGauge({ score }) {
  const color = score >= 80 ? "#22c55e" : score >= 50 ? "#f59e0b" : "#ef4444";
  const radius = 40;
  const circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;

  return (
    <div className="flex flex-col items-center">
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="10" />
        <circle cx="55" cy="55" r={radius} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 55 55)" style={{ transition: "stroke-dasharray 0.8s ease" }}
        />
        <text x="55" y="61" textAnchor="middle" fontSize="22" fontWeight="bold" fill={color}>{score}</text>
      </svg>
      <p className="text-sm font-semibold text-gray-600 -mt-1">
        {score >= 80 ? "Bon compliment" : score >= 50 ? "Millora necessària" : "Risc elevat"}
      </p>
    </div>
  );
}

function AreaCard({ icon: Icon, title, ok, total, color }) {
  const pct = total > 0 ? Math.round((ok / total) * 100) : 100;
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
      </div>
      <div className="flex items-end justify-between mb-2">
        <span className="text-2xl font-bold text-gray-900">{pct}%</span>
        <span className="text-xs text-gray-400">{ok}/{total} conformes</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: pct >= 80 ? "#22c55e" : pct >= 50 ? "#f59e0b" : "#ef4444" }}
        />
      </div>
    </div>
  );
}

export default function TechnicalDashboard({ fire, gas, equipment, electrical }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isOk = (el, dateField) => {
    if (el.status === "ok" || el.status === "operatiu") return true;
    if (!dateField || !el[dateField]) return el.status === "ok" || el.status === "operatiu";
    return new Date(el[dateField]) >= today;
  };

  const fireOk    = fire.filter(e => isOk(e, "next_revision_date")).length;
  const gasOk     = gas.filter(e => isOk(e, "next_inspection_date")).length;
  const equipOk   = equipment.filter(e => e.status === "operatiu").length;
  const electricOk = electrical.filter(e => e.status === "ok").length;

  const totalItems = fire.length + gas.length + equipment.length + electrical.length;
  const okItems    = fireOk + gasOk + equipOk + electricOk;
  const score      = totalItems > 0 ? Math.round((okItems / totalItems) * 100) : 100;

  // Upcoming alerts
  const allRevisions = [
    ...fire.map(e => ({ name: e.name, date: e.next_revision_date, type: "incendis" })),
    ...gas.map(e => ({ name: e.name, date: e.next_inspection_date, type: "gas" })),
    ...equipment.map(e => ({ name: e.name, date: e.next_maintenance_date, type: "equip" })),
    ...electrical.map(e => ({ name: e.name, date: e.next_inspection_date, type: "elèctrica" })),
  ].filter(e => e.date);

  const overdue = allRevisions.filter(e => new Date(e.date) < today);
  const soon30  = allRevisions.filter(e => {
    const d = new Date(e.date);
    return d >= today && Math.ceil((d - today) / 86400000) <= 30;
  });

  const alerts = [
    ...overdue.map(e => ({ ...e, level: "critical" })),
    ...soon30.map(e => ({ ...e, level: "warning" })),
  ].slice(0, 6);

  return (
    <div className="space-y-6">
      {/* Score + summary */}
      <div className="bg-gradient-to-br from-[#7B2D3F] to-[#9B4D5F] rounded-2xl p-6 text-white">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <ScoreGauge score={score} />
          <div className="flex-1">
            <h2 className="text-lg font-bold mb-1">Índex de Compliment Tècnic</h2>
            <p className="text-white/70 text-sm mb-4">Basat en {totalItems} elements registrats al local</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Incendis", ok: fireOk, total: fire.length },
                { label: "Gas", ok: gasOk, total: gas.length },
                { label: "Equips", ok: equipOk, total: equipment.length },
                { label: "Elèctrica", ok: electricOk, total: electrical.length },
              ].map(s => (
                <div key={s.label} className="bg-white/15 rounded-xl p-2 text-center">
                  <p className="text-lg font-bold">{s.total > 0 ? Math.round((s.ok / s.total) * 100) : 100}%</p>
                  <p className="text-xs text-white/70">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Area cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <AreaCard icon={ShieldCheck} title="Protecció incendis" ok={fireOk} total={fire.length} color="bg-red-500" />
        <AreaCard icon={ShieldAlert} title="Instal·lació gas" ok={gasOk} total={gas.length} color="bg-orange-500" />
        <AreaCard icon={CheckCircle2} title="Equips cuina" ok={equipOk} total={equipment.length} color="bg-blue-500" />
        <AreaCard icon={TrendingUp} title="Instal·lació elèctrica" ok={electricOk} total={electrical.length} color="bg-yellow-500" />
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            Alertes actives ({alerts.length})
          </h3>
          <div className="space-y-2">
            {alerts.map((a, i) => (
              <div key={`alert-${i}`} className={`flex items-center gap-3 p-3 rounded-xl border text-sm ${a.level === "critical" ? "bg-red-50 border-red-200 text-red-800" : "bg-orange-50 border-orange-200 text-orange-800"}`}>
                {a.level === "critical"
                  ? <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  : <Clock className="w-4 h-4 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{a.name}</span>
                  <span className="text-xs ml-2 opacity-70">{a.type}</span>
                </div>
                <span className="text-xs font-semibold flex-shrink-0">
                  {a.level === "critical"
                    ? `Caducada el ${new Date(a.date).toLocaleDateString("ca-ES")}`
                    : `${new Date(a.date).toLocaleDateString("ca-ES")}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {alerts.length === 0 && totalItems > 0 && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-2xl p-4">
          <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-green-800">Tot en ordre</p>
            <p className="text-sm text-green-600">Cap revisió pendent o caducada en els pròxims 30 dies.</p>
          </div>
        </div>
      )}
    </div>
  );
}